import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select, { components } from "react-select";
import "./LPGeneral.css";
import { Helmet } from "react-helmet-async";
/* ─────────────────────────────────────────────
   DATA CONSTANTS
───────────────────────────────────────────── */
const COUNTRIES = [
  { flag: "🇬🇧", name: "UK", selectLabel: "United Kingdom", meta: "8,000+ courses" },
  { flag: "🇺🇸", name: "USA", selectLabel: "USA", meta: "Top-ranked unis" },
  { flag: "🇨🇦", name: "Canada", selectLabel: "Canada", meta: "PR pathway" },
  { flag: "🇩🇪", name: "Germany", selectLabel: "Germany", meta: "Low/no tuition" },
  { flag: "🇫🇷", name: "France", selectLabel: "France", meta: "Affordable living" },
  { flag: "🇳🇱", name: "Netherlands", selectLabel: "Netherlands", meta: "English-taught" },
  { flag: "🇪🇸", name: "Spain", selectLabel: "Spain", meta: "Vibrant culture" },
  { flag: "🇮🇪", name: "Ireland", selectLabel: "Ireland", meta: "Post-study work visa" },
  { flag: "🇦🇺", name: "Australia", selectLabel: "Australia", meta: "Work while study" },
  { flag: "🇱🇻", name: "Latvia", selectLabel: "Latvia", meta: "Low cost EU degree" },
  { flag: "🇭🇺", name: "Hungary", selectLabel: "Hungary", meta: "Affordable EU option" },
  { flag: "🇳🇿", name: "New Zealand", selectLabel: "New Zealand", meta: "Quality of life" },
  { flag: "🇦🇪", name: "UAE", selectLabel: "UAE", meta: "Global hub campuses" },
  { flag: "🇵🇱", name: "Poland", selectLabel: "Poland", meta: "Budget-friendly EU" },
  { flag: "🇲🇾", name: "Malaysia", selectLabel: "Malaysia", meta: "Low cost, close to home" },
  { flag: "🇸🇬", name: "Singapore", selectLabel: "Singapore", meta: "Asia's education hub" },
];

const HERO_FLAG_NAMES = ["UK", "Canada", "Australia", "Germany", "Ireland"];

const FEATURES = [
  { ico: "🎯", title: "University Matching", desc: "AI-matched against your profile, budget, and goals" },
  { ico: "📝", title: "Application Support", desc: "SOPs, documents, and submissions handled for you" },
  { ico: "🛂", title: "Visa Guidance", desc: "98% visa success rate across our partner countries" },
  { ico: "🎓", title: "Scholarship Search", desc: "We actively match you to available scholarships" },
];

const STEPS = [
  { num: 1, title: "Tell us your goals", desc: "Share your background, budget, and target destination — takes 60 seconds." },
  { num: 2, title: "Get matched courses", desc: "We shortlist universities and programmes that fit your profile." },
  { num: 3, title: "Apply with our support", desc: "We help you prepare documents, SOPs, and submit applications." },
  { num: 4, title: "Visa & departure", desc: "Visa filing, pre-departure briefing, and accommodation support." },
];

const TESTIMONIALS = [
  {
    text: "\"FlyUrDream matched me with a university I hadn't even considered — and got my visa approved in 3 weeks. I couldn't have done this alone.\"",
    initials: "AS",
    name: "Arjun Sharma",
    meta: "MSc Computer Science, University of Manchester",
  },
  {
    text: "\"They found me a £4,000 scholarship I didn't know existed. The whole process felt effortless.\"",
    initials: "PN",
    name: "Priya Nair",
    meta: "MBA, University of Toronto",
  },
];

const FAQS = [
  { q: "Is the counselling really free?", a: "Yes — FlyUrDream's counselling, university matching, and application support are 100% free for students. We're paid by partner universities, not by you." },
  { q: "How long does the process take?", a: "Most students go from first call to offer letter within 4–8 weeks, depending on the destination and intake." },
  { q: "Do you help with visas?", a: "Yes, end-to-end — document checklist, application filing, and interview preparation where required." },
];

const COURSE_OPTIONS = [
  "Business & Management", "MBA", "Computer Science / IT", "Engineering",
  "Data Science & Analytics", "Healthcare & Medicine", "Nursing", "Law",
  "Design & Creative Arts", "Architecture", "Hospitality & Tourism",
  "Finance & Accounting", "Economics", "Education & Teaching",
  "Social Sciences", "Psychology", "Science & Research",
  "Agriculture & Environment", "Media & Communication", "Public Health", "Other",
];

const STUDY_LEVELS = ["Pre-sessional English", "Foundation", "Bachelors Degree", "Masters Degree", "PHD", "DBA"];

const PHONE_CODES = ["+91", "+44", "+1", "+971"];

/* ─────────────────────────────────────────────
   COUNTRY SELECT RENDERERS (with flag icons, form only)
───────────────────────────────────────────── */
const CountryOption = (props) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="lpg-select-flex">
        <img src={`/countrylogos/${data.value}.png`} alt="" className="lpg-flag-icon" />
        <span>{data.label}</span>
      </div>
    </components.Option>
  );
};

const CountrySingleValue = (props) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="lpg-select-flex">
        <img src={`/countrylogos/${data.value}.png`} alt="" className="lpg-flag-icon" />
        <span>{data.label}</span>
      </div>
    </components.SingleValue>
  );
};

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
export default function LPGeneral({ logoSrc = "../FUD-logo1-1-1-1.png" }) {
  const formSectionRef = useRef(null);

  const [showPrefillNote, setShowPrefillNote] = useState(false);
  const [enquiryId, setEnquiryId] = useState(null);

  const [fName, setFName] = useState("");
  const [fEmail, setFEmail] = useState("");
  const [fPhoneCode, setFPhoneCode] = useState("+91");
  const [fPhone, setFPhone] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fCourse, setFCourse] = useState("");
  const [fLevel, setFLevel] = useState("");
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [consentWhatsapp, setConsentWhatsapp] = useState(false);

  const [prefilled, setPrefilled] = useState({ name: false, email: false, phone: false, country: false });

  const [submitting, setSubmitting] = useState(false);

  // ── Countries (for Destination flag-select in the form) ──
  const [apiCountries, setApiCountries] = useState([]);
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/GetCountries`);
        const trimmedCountries = response.data.map((c) => ({
          ...c,
          countryCode: c.countryCode?.trim().toLowerCase(),
        }));
        setApiCountries(trimmedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, []);

  const countryOptions = apiCountries.map((c) => ({
    value: c.countryCode,
    label: c.countryName,
  }));

  const handleCountryChange = (selected) => {
    setFCountry(selected ? selected.label : "");
  };

  // ── IP address capture, same pattern as RecruitmentPartner ──
  const [ipAddress, setIpAddress] = useState("");
  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        setIpAddress(response.data.ip);
      } catch (error) {
        console.error("Error fetching IP address:", error);
        setIpAddress("Unable to fetch IP");
      }
    };
    fetchIpAddress();
  }, []);

  /* ── URL-Parameter Pre-Fill ──
     Example usage: this-page?name=Arjun%20Sharma&email=arjun@gmail.com&phone=9876543210&country=UK&enquiry_id=FYD-ENQ-10482
     Used for retargeting links (WhatsApp/email follow-ups) where FlyUrDream already knows the lead.
     Country matching now waits for apiCountries (GetCountries) to load, then matches by
     countryName (partial, case-insensitive). */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let didPrefill = false;
    const nextPrefilled = { name: false, email: false, phone: false, country: false };

    const nameVal = params.get("name");
    if (nameVal) { setFName(nameVal); nextPrefilled.name = true; didPrefill = true; }

    const emailVal = params.get("email");
    if (emailVal) { setFEmail(emailVal); nextPrefilled.email = true; didPrefill = true; }

    const phoneVal = params.get("phone");
    if (phoneVal) { setFPhone(phoneVal); nextPrefilled.phone = true; didPrefill = true; }

    const enquiryIdVal = params.get("enquiry_id");
    if (enquiryIdVal) { setEnquiryId(enquiryIdVal); didPrefill = true; }

    if (didPrefill) {
      setPrefilled((prev) => ({ ...prev, ...nextPrefilled }));
      setShowPrefillNote(true);
    }
  }, []);

  // Country param needs apiCountries loaded first — separate effect, runs once countries arrive
  useEffect(() => {
    if (apiCountries.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const countryVal = params.get("country");
    if (!countryVal) return;

    const match = apiCountries.find((c) =>
      c.countryName?.toLowerCase().includes(countryVal.toLowerCase())
    );
    if (match) {
      setFCountry(match.countryName);
      setPrefilled((prev) => ({ ...prev, country: true }));
      setShowPrefillNote(true);
    }
  }, [apiCountries]);

  const scrollToForm = () => {
    formSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ── Input handlers: restrict characters at typing time ──
  const handleNameChange = (e) => {
    // letters and spaces only — no digits, no special characters
    const onlyLetters = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    setFName(onlyLetters);
  };

  const handlePhoneChange = (e) => {
    // digits only — no alphabets, no special characters
    const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFPhone(onlyDigits);
  };

  // ── Validation, same style as RecruitmentPartner's validateForm ──
  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;
    const nameRegex = /^[a-zA-Z\s]+$/;

    if (!fName.trim()) {
      toast.error("Please enter your name");
      return false;
    }
    if (!nameRegex.test(fName.trim())) {
      toast.error("Name should only contain letters");
      return false;
    }
    if (!fEmail.trim()) {
      toast.error("Please enter your email address");
      return false;
    }
    if (!emailRegex.test(fEmail)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!fPhone.trim()) {
      toast.error("Please enter your phone number");
      return false;
    }
    if (!phoneRegex.test(fPhone)) {
      toast.error("Phone number must be exactly 10 digits");
      return false;
    }
    if (!fCountry) {
      toast.error("Please select where you want to study");
      return false;
    }
    if (!fCourse) {
      toast.error("Please select your course interest");
      return false;
    }
    if (!fLevel) {
      toast.error("Please select your study level");
      return false;
    }
    if (!consentMarketing) {
      toast.error("Please accept the marketing consent to proceed");
      return false;
    }
    // if (!consentWhatsapp) {
    //   toast.error("Please accept the WhatsApp updates consent to proceed");
    //   return false;
    // }
    return true;
  };

  // ── Submit: PATCH if enquiryId exists (update), else POST (create) ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

const payload = {
  Enquiry_Id: enquiryId ? Number(enquiryId) || 0 : null,
  CompanyId: { CompanyID: window.location.origin === "https://flyurdream.com" ? 160 : 2, 
          UCompanyId: window.location.origin === "https://flyurdream.com" ? "GuidP160" : "GuidP2",
  },
        UCompanyId: window.location.origin === "https://flyurdream.com" ? "GuidP160" : "GuidP2",
  FirstName: fName,
  LastName: "",
  Mobile_Number: fPhone,
  Country_Code: fPhoneCode,
  Email: fEmail,
  Preferred_Contact_Time: null,
  Lead_Type: { FieldType: "General" },
  Lead_Stage: { FieldType: "New" },
    Source:{

fieldType: "Manual"
  },
  Source_Company: "FlyUrDream",
  CollegeVisit_CollegeName: null,
  CollegeVisit_City: null,
  CollegeVisit_Date: null,
  Batch: "",
  Captured_By: null,
  Captured_By_System: "LPGeneral",
  Assigned_To: null,
  Converted_By: null,
  Converted_Application_Id: null,
  Is_Consented: Boolean(consentMarketing),
  Consent_Via: consentWhatsapp ? { FieldType: "WhatsApp" } : null,
  Consent_Financial_Share: null,
  Consent_WhatsAppUpdates: Boolean(consentWhatsapp),
  Preferred_Country: null,
  Preferred_CountryName: fCountry,
  Course_Interest: fCourse,
  Study_Level: fLevel,
  University: null,
  University_Interest: "",
  Intake: null,
  Loan_Amount_Range: null,
  Has_Collateral: null,
  Notes: "",
  CreatedBy: null,
  IsArchived: false,
};

console.log("before", payload);

const isUpdate = Boolean(enquiryId);
const url = isUpdate
  ? `${process.env.REACT_APP_API_BASE_URL}/UpdateEnquiry`
  : `${process.env.REACT_APP_API_BASE_URL}/CreateEnquiry`;

setSubmitting(true);
try {
  const response = await axios.post(url, payload);

  toast.success(response.data.message || "Thanks! Our counsellor will reach out shortly.");

  // Store returned enquiry_Id so a resubmit in the same session updates
  // instead of creating a duplicate record
  if (response.data?.enquiry_Id) {
    setEnquiryId(response.data.enquiry_Id);
  }

  console.log("after", response.data);

  // ── Reset form after successful upsert ──
  setFName("");
  setFEmail("");
  setFPhoneCode("+91");
  setFPhone("");
  setFCountry("");
  setFCourse("");
  setFLevel("");
  setConsentMarketing(false);
  setConsentWhatsapp(false);
  setPrefilled({ name: false, email: false, phone: false, country: false });
  setShowPrefillNote(false);
} catch (error) {
  toast.error("Error submitting enquiry. Please try again.");
  console.error("Enquiry submit failed:", error);
} finally {
  setSubmitting(false);
}
  };

  return (
    <div className="lpg-page">
      {/* ── HEADER ── */}
      <Helmet>
        <title>Study Abroad 2026 — Free Counselling | FlyUrDream</title>
        <meta name="description" content="Get matched with the right university, course, and visa pathway across 50+ countries. 100% free counselling, 98% visa success rate." />
        <link rel="canonical" href="https://flyurdream.com/LPGeneral" />

        <meta property="og:title" content="Study Abroad 2026 — Free Counselling | FlyUrDream" />
        <meta property="og:description" content="8,000+ universities, 50+ countries, 98% visa success rate. Get your free assessment today." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://flyurdream.com/LPGeneral" />

        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* ── HERO ── */}
      <div className="lpg-hero">
         <div  className="lpg-header-cta" >
         {/* <a href="#lpg-form" className="lpg-header-cta-a" onClick={(e) => { e.preventDefault(); scrollToForm(); }}>
 Free Counselling →
         </a> */}
        </div>
        <div className="lpg-hero-inner">
          <div className="lpg-hero-badge">✦ 100% Free Counselling</div>
          <h1>
            Study Abroad in 2026.<br />
            Your Dream University <span>Is Closer Than You Think.</span>
          </h1>
          <p className="lpg-hero-sub">
            Get matched with the right course, university, and visa pathway — with expert guidance from application to arrival.
          </p>
          <div className="lpg-hero-flags">
            {COUNTRIES.filter((c) => HERO_FLAG_NAMES.includes(c.name)).map((c) => (
              <div className="lpg-hflag" key={c.name}>{c.flag} {c.name}</div>
            ))}
            <div className="lpg-hflag">+ 45 more</div>
          </div>
          <div className="lpg-hero-stats">
            <div className="lpg-hflag"><div className="lpg-hstat-num">8,000+</div><div className="lpg-hstat-lbl">Universities</div></div>
            <div className="lpg-hflag"><div className="lpg-hstat-num">50+</div><div className="lpg-hstat-lbl">Countries</div></div>
            <div className="lpg-hflag"><div className="lpg-hstat-num">98%</div><div className="lpg-hstat-lbl">Visa Success Rate</div></div>
          </div>
        </div>
      </div>

      {/* ── MAIN LAYOUT ── */}
      <div className="lpg-layout" id="lpg-form" ref={formSectionRef}>
        <div className="lpg-content-col">

          {/* Why FlyUrDream */}
          <section className="lpg-sec-block">
            <div className="lpg-sec-label">Why FlyUrDream</div>
            <h2>Everything you need, in one place.</h2>
            <p>From choosing a course to landing in your new city — we handle the entire journey so you don't have to manage ten different agents.</p>
            <div className="lpg-feature-grid">
              {FEATURES.map((f) => (
                <div className="lpg-feature-card" key={f.title}>
                  <div className="lpg-feature-ico">{f.ico}</div>
                  <div className="lpg-feature-title">{f.title}</div>
                  <div className="lpg-feature-desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Top Destinations */}
          <section className="lpg-sec-block">
            <div className="lpg-sec-label">Top Destinations</div>
            <h2>Where students are heading in 2026.</h2>
            <div className="lpg-country-grid">
              {COUNTRIES.map((c) => (
                <div className="lpg-country-card" key={c.name}>
                  <div className="lpg-country-flag">{c.flag}</div>
                  <div className="lpg-country-name">{c.name}</div>
                  <div className="lpg-country-meta">{c.meta}</div>
                </div>
              ))}
            </div>
          </section>

          {/* How It Works */}
          <section className="lpg-sec-block">
            <div className="lpg-sec-label">How It Works</div>
            <h2>From enquiry to enrolment in 4 steps.</h2>
            <div className="lpg-step-list">
              {STEPS.map((s) => (
                <div className="lpg-step-item" key={s.num}>
                  <div className="lpg-step-num">{s.num}</div>
                  <div>
                    <div className="lpg-step-title">{s.title}</div>
                    <div className="lpg-step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Student Stories */}
          <section className="lpg-sec-block">
            <div className="lpg-sec-label">Student Stories</div>
            <h2>Real students, real outcomes.</h2>
            {TESTIMONIALS.map((t) => (
              <div className="lpg-testimonial-card" key={t.name}>
                <div className="lpg-testi-text">{t.text}</div>
                <div className="lpg-testi-author">
                  <div className="lpg-testi-avatar">{t.initials}</div>
                  <div>
                    <div className="lpg-testi-name">{t.name}</div>
                    <div className="lpg-testi-meta">{t.meta}</div>
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* FAQs */}
          <section className="lpg-sec-block">
            <div className="lpg-sec-label">FAQs</div>
            <h2>Common questions.</h2>
            {FAQS.map((f) => (
              <div className="lpg-faq-item" key={f.q}>
                <div className="lpg-faq-q">{f.q}</div>
                <div className="lpg-faq-a">{f.a}</div>
              </div>
            ))}
          </section>

        </div>

        {/* ── FORM CARD ── */}
        <div className="lpg-form-card">
          <h3>Get Your Free Assessment</h3>
          <div className="lpg-fsub">Takes 60 seconds · No cost, ever</div>
          <form onSubmit={handleSubmit}>
            {showPrefillNote && (
              <div className="lpg-prefill-note lpg-show">✅ Welcome back — we've pre-filled what we already know</div>
            )}

            <div className="lpg-f-row">
              <label>FULL NAME <span className="lpg-required">*</span></label>
              <input
                type="text"
                placeholder="Enter your full name"
                className={prefilled.name ? "lpg-prefilled" : ""}
                value={fName}
                onChange={handleNameChange}
              />
            </div>
            <div className="lpg-f-row">
              <label>EMAIL <span className="lpg-required">*</span></label>
              <input
                type="email"
                placeholder="you@email.com"
                className={prefilled.email ? "lpg-prefilled" : ""}
                value={fEmail}
                onChange={(e) => setFEmail(e.target.value)}
              />
            </div>
            <div className="lpg-f-row">
              <label>PHONE / WHATSAPP <span className="lpg-required">*</span></label>
              <div className="lpg-phone-grid">
                <select value={fPhoneCode} onChange={(e) => setFPhoneCode(e.target.value)}>
                  {PHONE_CODES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <input
                  type="text"
                  placeholder="98765 43210"
                  className={prefilled.phone ? "lpg-prefilled" : ""}
                  value={fPhone}
                  onChange={handlePhoneChange}
                  inputMode="numeric"
                  maxLength={10}
                />
              </div>
            </div>
            <div className="lpg-f-row">
              <label>WHERE DO YOU WANT TO STUDY? <span className="lpg-required">*</span></label>
              <Select
                classNamePrefix="lpg-country-select"
                options={countryOptions}
                components={{ Option: CountryOption, SingleValue: CountrySingleValue }}
                value={countryOptions.find((opt) => opt.label === fCountry) || null}
                onChange={handleCountryChange}
                placeholder="Select destination"
                isSearchable
                className={prefilled.country ? "lpg-prefilled" : ""}
              />
            </div>
            <div className="lpg-f-row">
              <label>COURSE INTEREST <span className="lpg-required">*</span></label>
              <select value={fCourse} onChange={(e) => setFCourse(e.target.value)}>
                <option value="">Select course area</option>
                {COURSE_OPTIONS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="lpg-f-row">
              <label>STUDY LEVEL <span className="lpg-required">*</span></label>
              <select value={fLevel} onChange={(e) => setFLevel(e.target.value)}>
                <option value="">Select level</option>
                {STUDY_LEVELS.map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>
            <div className="lpg-consent-block">
              <div className="lpg-consent-row">
                <input
                  type="checkbox"
                  id="lpg-consent-marketing"
                  checked={consentMarketing}
                  onChange={(e) => setConsentMarketing(e.target.checked)}
                />
                <label htmlFor="lpg-consent-marketing">
                  I consent to receiving information (by email, phone, or SMS/WhatsApp) about courses and services from FlyUrDream and its partner universities, in line with the <a href="/privacy-policy">Privacy Policy</a>. <span className="lpg-required">*</span>
                </label>
              </div>
              <div className="lpg-consent-row">
                <input
                  type="checkbox"
                  id="lpg-consent-whatsapp"
                  checked={consentWhatsapp}
                  onChange={(e) => setConsentWhatsapp(e.target.checked)}
                />
                <label htmlFor="lpg-consent-whatsapp">
                  I would like to receive WhatsApp updates about my application status, course matches, and upcoming deadlines. <span className="lpg-required"></span>
                </label>
              </div>
            </div>
            <button type="submit" className="lpg-submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Get My Free Assessment →"}
            </button>
          </form>
          <div className="lpg-trust-line">🔒 Your data is safe and never shared</div>
        </div>
      </div>

      {/* ── STICKY MOBILE CTA ── */}
      {/* <div className="lpg-sticky-mobile-cta">
        <button onClick={scrollToForm}>Get Free Assessment →</button>
      </div> */}

      {/* ── FOOTER ── */}
      <div className="lpg-footer">
        FlyUrDream · Free overseas education counselling<br />
        <a href="/privacy-policy">Privacy Policy</a> · <a href="/terms">Terms</a> · study@flyurdream.com
      </div>

      <ToastContainer />
    </div>
  );
}